import {
	Box,
	Divider,
	Typography,
	Stack,
	Paper,
	Chip,
	Button,
} from '@mui/material'
import React from 'react'
import { ManualChangeOrderInfo } from '../../types/Work/ChangeOrder'
import EditIcon from '@mui/icons-material/Edit'
import { useChangeOrderContext } from './ChangeOrderContext'
import { formatLocaleShortDateTime } from '../../services/CommonServices/DataFormatters'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
interface ChangeOrderDetailsProps {
	manualInfo: ManualChangeOrderInfo
	revisionNumber?: number
	parentId?: number
	finalRevision?: boolean
	createdAt?: Date
}

export default function ChangeOrderDetails({
	manualInfo,
	revisionNumber,
	parentId,
	finalRevision = false,
	createdAt,
}: ChangeOrderDetailsProps) {
	const totalCost =
		manualInfo.laborCost +
		manualInfo.materialCost +
		manualInfo.equipmentCost +
		manualInfo.subContractorCost

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
		}).format(value)
	}
	const { setDetailsToRevise } = useChangeOrderContext()
	const { projectTimeZone } = useProjectFeaturesContext()

	const handleRevise = () => {
		setDetailsToRevise({ ...manualInfo, changeOrderId: parentId ?? manualInfo.changeOrderId })
	}
	const hasCostImpact = manualInfo.laborCost > 0 || manualInfo.materialCost > 0 || manualInfo.equipmentCost > 0 || manualInfo.subContractorCost > 0
	const hasScheduleImpact = manualInfo.scheduleImpact > 0
	return (
		<Paper variant='outlined' sx={{ p: 2, backgroundColor: 'background.paper' }}>
			<Stack spacing={2}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'space-between' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
							{revisionNumber ? `Revision ${revisionNumber}` : 'Change Order Details'}
						</Typography>
						{createdAt && (
							<Typography variant='body2' color='text.secondary' sx={{ ml: 1 }}>
								{formatLocaleShortDateTime(createdAt, projectTimeZone ?? 'UTC')}
							</Typography>
						)}
						{/* Impact Indicators */}
						<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
							{hasCostImpact && (
								<Chip
									label='Cost Impact'
									color='warning'
									size='small'
									sx={{ height: 24, fontSize: '0.75rem' }}
								/>
							)}
							{hasScheduleImpact && (
								<Chip
									label='Schedule Impact'
									color='info'
									size='small'
									sx={{ height: 24, fontSize: '0.75rem' }}
								/>
							)}
						</Box>
					</Box>
					{finalRevision && (
						<Box>
							<Button variant="outlined" size="small"
								startIcon={<EditIcon />} sx={{ color: 'primary.main' }} onClick={handleRevise}>
								Revise
							</Button>
						</Box>
					)}
				</Box>
				<Divider />

				{/* Description */}
				{manualInfo.description && (
					<Box>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
							Description:
						</Typography>
						<Typography variant='body1' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
							{manualInfo.description}
						</Typography>
					</Box>
				)}

				{/* Work Items */}
				{manualInfo.workItems && manualInfo.workItems.length > 0 && (
					<Box>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
							Work Items:
						</Typography>
						<Stack spacing={0.5}>
							{manualInfo.workItems.map((workItem) => (
								<Box
									key={workItem.id}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										py: 0.5,
									}}
								>
									<Typography variant="body2" sx={{ mr: 1 }}>•</Typography>
									<Typography variant='body2' sx={{ fontWeight: 500 }}>
										{workItem.path}
									</Typography>
								</Box>
							))}
						</Stack>
					</Box>
				)}

				{/* Schedule Impact */}
				{hasScheduleImpact && (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant='body2' color='text.secondary'>
							Schedule Impact:
						</Typography>
						<Typography variant='body2' sx={{ fontWeight: 500 }}>
							{manualInfo.scheduleImpact} {manualInfo.scheduleImpactUnit}
						</Typography>
					</Box>
				)}

				{/* Cost Impact */}
				{hasCostImpact && (
					<Box>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
							Cost Breakdown:
						</Typography>
						<Stack spacing={1}>
							{manualInfo.laborCost > 0 && (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										py: 0.5,
									}}
								>
									<Typography variant='body2' color='text.secondary'>
										Labor Cost:
									</Typography>
									<Typography variant='body2' sx={{ fontWeight: 500 }}>
										{formatCurrency(manualInfo.laborCost)}
									</Typography>
								</Box>
							)}
							{manualInfo.materialCost > 0 && (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										py: 0.5,
									}}
								>
									<Typography variant='body2' color='text.secondary'>
										Material Cost:
									</Typography>
									<Typography variant='body2' sx={{ fontWeight: 500 }}>
										{formatCurrency(manualInfo.materialCost)}
									</Typography>
								</Box>
							)}
							{manualInfo.equipmentCost > 0 && (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										py: 0.5,
									}}
								>
									<Typography variant='body2' color='text.secondary'>
										Equipment Cost:
									</Typography>
									<Typography variant='body2' sx={{ fontWeight: 500 }}>
										{formatCurrency(manualInfo.equipmentCost)}
									</Typography>
								</Box>
							)}
							{manualInfo.subContractorCost > 0 && (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										py: 0.5,
									}}
								>
									<Typography variant='body2' color='text.secondary'>
										Subcontractor Cost:
									</Typography>
									<Typography variant='body2' sx={{ fontWeight: 500 }}>
										{formatCurrency(manualInfo.subContractorCost)}
									</Typography>
								</Box>
							)}
							{totalCost > 0 && (
								<>
									<Divider />
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											py: 0.5,
										}}
									>
										<Typography variant='body2' sx={{ fontWeight: 600 }}>
											Total Cost:
										</Typography>
										<Typography variant='body2' sx={{ fontWeight: 600 }}>
											{formatCurrency(totalCost)}
										</Typography>
									</Box>
								</>
							)}
						</Stack>
					</Box>
				)}
			</Stack>
		</Paper>
	)
}
